import { useMemo, RefObject } from "react";
import { useAppStore } from "@/stores/appStore";
import { useFieldConfig } from "@/hooks/useFieldConfig";
import { InitiativeCard } from "./InitiativeCard";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { formatMonthLabel } from "@/utils/monthLabel";
import { MonthLabel } from "@/components/ui/MonthLabel";
import { buildWeekColumns, parseLocalDate, type WeekColumn } from "@/utils/weeks";
import type { Initiative, SubProduct } from "@/types";

interface TimelineWeekViewProps {
  initiatives?: Initiative[];
  containerRef?: RefObject<HTMLDivElement>;
}

const CARD_HEIGHT = 3; // rem
const CARD_GAP = 0.5; // rem
const ROW_PADDING = 0.75; // rem
// Occupied weeks are wide enough to show the pill's title/status/date; empty (incl. future) weeks
// are slim and greyed — mirroring the monthly view's full-timeline behaviour.
const WEEK_WIDE = 160; // px — width for a week column containing at least one date-mode initiative
const WEEK_EMPTY = 72; // px — narrow width for weeks with no initiatives, just enough for the date label
const MIN_BAR_W = 165; // px — floor so single-day / open-ended / date-range pills still show their content
const DAY_MS = 24 * 60 * 60 * 1000;

// A date-mode initiative is one entered with an exact start date and day precision.
export function isDateMode(init: Initiative): boolean {
  return (init as any).datePrecision === "day" && !!init.startDate;
}

function dateBounds(init: Initiative): { start: Date; end: Date } {
  const start = parseLocalDate(init.startDate as string);
  const end = init.endDate ? parseLocalDate(init.endDate) : start;
  return { start, end: end < start ? start : end };
}

export function TimelineWeekView({ initiatives: filteredInitiatives, containerRef }: TimelineWeekViewProps) {
  const fc = useFieldConfig();
  const { products, initiatives: allInitiatives, quarters, classifications, openViewInitiativeModal } = useAppStore();

  const initiatives = filteredInitiatives || allInitiatives;

  // Only date-mode initiatives appear on the weekly grid (month-mode has no day precision).
  const dateModeInitiatives = useMemo(
    () => initiatives.filter(isDateMode),
    [initiatives],
  );
  const hiddenMonthModeCount = initiatives.length - dateModeInitiatives.length;

  // Week columns span the FULL store timeline so every week of every month is always visible,
  // whether or not it contains a date-mode initiative — unlike the Monthly/Quarterly grid, weeks
  // are never narrowed or hidden just because they're empty.
  const weekCols: WeekColumn[] = useMemo(() => {
    if (quarters.length === 0) return [];
    const sorted = [...quarters].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
    return buildWeekColumns(
      parseLocalDate(sorted[0].startDate),
      parseLocalDate(sorted[sorted.length - 1].endDate),
    );
  }, [quarters]);

  // Which weeks contain at least one date-mode initiative.
  const occupied = useMemo(() => {
    const set = new Set<string>();
    dateModeInitiatives.forEach((init) => {
      const { start, end } = dateBounds(init);
      weekCols.forEach((w) => {
        if (end.getTime() >= w.monday.getTime() && start.getTime() <= w.sunday.getTime()) set.add(w.key);
      });
    });
    return set;
  }, [weekCols, dateModeInitiatives]);

  // Occupied weeks render wide; empty (incl. future) weeks render narrow and greyed.
  const widthOf = (w: WeekColumn) => (occupied.has(w.key) ? WEEK_WIDE : WEEK_EMPTY);

  // Cumulative left-offset (px) of each week — needed to position pills over mixed-width columns.
  const weekOffsets = useMemo(() => {
    const arr: number[] = [];
    let x = 0;
    weekCols.forEach((w, i) => { arr[i] = x; x += widthOf(w); });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekCols, occupied]);

  const totalWidth = useMemo(
    () => weekCols.reduce((s, w) => s + widthOf(w), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekCols, occupied],
  );

  // Index of the week column that contains a given date.
  const weekIndexOf = (d: Date): number => {
    const t = d.getTime();
    for (let i = 0; i < weekCols.length; i++) {
      if (t >= weekCols[i].monday.getTime() && t <= weekCols[i].sunday.getTime()) return i;
    }
    return weekCols.length === 0 ? 0 : t < weekCols[0].monday.getTime() ? 0 : weekCols.length - 1;
  };

  // Group date-mode initiatives by sub-product, sorted by start date then creation time.
  const initiativesBySubProduct = useMemo(() => {
    const grouped = new Map<string, Initiative[]>();
    dateModeInitiatives.forEach((init) => {
      const key = init.subProductId;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(init);
    });
    grouped.forEach((inits) => {
      inits.sort((a, b) => {
        const sa = dateBounds(a).start.getTime();
        const sb = dateBounds(b).start.getTime();
        return sa - sb || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    });
    return grouped;
  }, [dateModeInitiatives]);

  const subProductsByProduct = useMemo(() => {
    const grouped = new Map<string, SubProduct[]>();
    products.forEach((product) => {
      if (product.subProducts) grouped.set(product.id, product.subProducts);
    });
    return grouped;
  }, [products]);

  const getProductInitiativeCount = (productId: string): number => {
    const subProducts = subProductsByProduct.get(productId) || [];
    return subProducts.reduce(
      (count, sp) => count + (initiativesBySubProduct.get(sp.id)?.length || 0),
      0,
    );
  };

  // Map a week to the store month (by the week's Monday) → keeps the existing month labels.
  const monthForWeek = (w: WeekColumn) =>
    quarters.find((q) => {
      const s = parseLocalDate(q.startDate);
      const e = parseLocalDate(q.endDate);
      return w.monday.getTime() >= s.getTime() && w.monday.getTime() <= e.getTime();
    });

  // Month band — consecutive weeks sharing a store month.
  const monthBands = useMemo(() => {
    const cells: { key: string; label: string; colSpan: number }[] = [];
    let i = 0;
    while (i < weekCols.length) {
      const m = monthForWeek(weekCols[i]);
      const label = m ? formatMonthLabel(m.fullLabel) : "";
      const groupKey = m?.key ?? `none-${i}`;
      let colSpan = 1;
      let j = i + 1;
      while (j < weekCols.length && (monthForWeek(weekCols[j])?.key ?? `none-${j}`) === groupKey) {
        colSpan++;
        j++;
      }
      cells.push({ key: `${groupKey}-${i}`, label, colSpan });
      i = j;
    }
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekCols, quarters]);

  const gridTemplate = useMemo(
    () => `15rem ${weekCols.map((w) => `${widthOf(w)}px`).join(" ")}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekCols, occupied],
  );

  const calculateRowHeight = (laneCount: number): number =>
    laneCount === 0 ? 5 : ROW_PADDING + laneCount * (CARD_HEIGHT + CARD_GAP) + ROW_PADDING;

  const handleInitiativeClick = (initiative: Initiative) => openViewInitiativeModal(initiative);

  // Empty state — nothing date-scheduled in the current filter.
  if (weekCols.length === 0 || dateModeInitiatives.length === 0) {
    return (
      <div ref={containerRef} className="h-full flex flex-col items-center justify-center text-center px-6">
        <p className="text-text-secondary text-sm max-w-xl">
          Found {hiddenMonthModeCount} {hiddenMonthModeCount === 1 ? "record" : "records"} — switch to Monthly or Quarterly to see them.
          <br />
          Weekly shows only initiatives entered with exact dates (Date mode).
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="relative min-w-max">
          {/* Sticky 2-tier header: Month band → Week row */}
          <div className="sticky top-0 z-30">
            {/* Month band */}
            <div className="grid gap-0" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="sticky left-0 z-40 bg-background-secondary border-b border-r border-gray-200 py-2 px-4 font-semibold text-text-primary text-sm">
                {fc.label('product')} / {fc.label('subProduct')}
              </div>
              {monthBands.map((cell) => (
                <div
                  key={cell.key}
                  className="bg-background-secondary border-b border-l border-gray-200 py-2 px-1 text-center whitespace-nowrap overflow-hidden font-semibold text-text-primary text-sm"
                  style={{ gridColumn: `span ${cell.colSpan}` }}
                >
                  <MonthLabel value={cell.label} />
                </div>
              ))}
            </div>

            {/* Week row */}
            <div className="grid gap-0" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="sticky left-0 z-40 bg-background-secondary border-b border-r border-gray-200" />
              {weekCols.map((w) => {
                const isOcc = occupied.has(w.key);
                return (
                  <div
                    key={w.key}
                    className={`border-b border-l border-gray-200 py-1.5 px-1 text-center whitespace-nowrap overflow-hidden text-xs ${
                      isOcc
                        ? "bg-background-secondary font-medium text-text-secondary"
                        : "bg-background-secondary font-normal text-gray-400"
                    }`}
                  >
                    {w.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Products and Sub-products */}
          {products.map((product) => {
            const subProducts = subProductsByProduct.get(product.id) || [];
            const productInitiativeCount = getProductInitiativeCount(product.id);
            if (productInitiativeCount === 0) return null;

            return (
              <div key={product.id}>
                {/* Product header row */}
                <div className="grid gap-0 bg-blue-50/50 border-b border-gray-100" style={{ gridTemplateColumns: gridTemplate }}>
                  <div className="sticky left-0 z-10 bg-blue-50/50 py-2 px-4 font-semibold text-text-primary text-sm border-r border-gray-100 flex items-center gap-2 min-w-0">
                    {product.icon && ICON_MAP[product.icon] && (
                      <span style={{ color: product.iconColor || "#6B7280" }}>
                        {(() => {
                          const IconComponent = ICON_MAP[product.icon];
                          return <IconComponent className="w-4 h-4 flex-shrink-0" />;
                        })()}
                      </span>
                    )}
                    <span className="truncate min-w-0" title={product.name}>{product.name}</span>
                    <span className="text-xs font-normal text-text-secondary flex-shrink-0" title={`${fc.label('initiative')}s Found`}>
                      - {productInitiativeCount}
                    </span>
                  </div>
                  {weekCols.map((w) => (
                    <div key={w.key} className="border-l border-gray-100" />
                  ))}
                </div>

                {/* Sub-product rows */}
                {subProducts.map((subProduct) => {
                  const subProductInitiatives = initiativesBySubProduct.get(subProduct.id) || [];
                  if (subProductInitiatives.length === 0) return null;

                  // Compute px metrics over mixed-width columns via the offset map, then greedy
                  // lane-pack by horizontal overlap. A pill floors at MIN_BAR_W so single-day /
                  // open-ended items still show their title, status, and date — but never past the
                  // right edge of the week its date range actually ends in, so a pill can't visually
                  // spill into a later week it doesn't belong to (it may still overlap an EARLIER,
                  // narrower/greyed empty week when the floor pushes the left edge back).
                  const laneEndPx: number[] = [];
                  const placed = subProductInitiatives.map((init) => {
                    const { start, end } = dateBounds(init);
                    const swi = weekIndexOf(start);
                    const ewi = weekIndexOf(end);
                    const startDay = Math.min(6, Math.max(0, Math.round((start.getTime() - weekCols[swi].monday.getTime()) / DAY_MS)));
                    const endDay = Math.min(6, Math.max(0, Math.round((end.getTime() - weekCols[ewi].monday.getTime()) / DAY_MS)));
                    const left = weekOffsets[swi] + (startDay / 7) * widthOf(weekCols[swi]);
                    const right = weekOffsets[ewi] + ((endDay + 1) / 7) * widthOf(weekCols[ewi]);
                    const endWeekRightEdge = weekOffsets[ewi] + widthOf(weekCols[ewi]);
                    let width = Math.max(MIN_BAR_W, right - left);
                    width = Math.min(width, endWeekRightEdge - left);
                    if (left + width > totalWidth) width = Math.max(60, totalWidth - left);
                    return { init, left, width };
                  });

                  const laneOf = new Map<string, number>();
                  for (const p of placed) {
                    let lane = laneEndPx.findIndex((end) => p.left >= end - 0.5);
                    if (lane === -1) {
                      lane = laneEndPx.length;
                      laneEndPx.push(p.left + p.width);
                    } else {
                      laneEndPx[lane] = p.left + p.width;
                    }
                    laneOf.set(p.init.id, lane);
                  }
                  const laneCount = Math.max(1, laneEndPx.length);
                  const rowHeight = calculateRowHeight(laneCount);

                  return (
                    <div
                      key={subProduct.id}
                      className="grid gap-0 border-b border-gray-100"
                      style={{ gridTemplateColumns: gridTemplate, height: `${rowHeight}rem` }}
                    >
                      <div
                        className="sticky left-0 z-10 p-3 pl-8 bg-background-primary text-sm text-text-secondary truncate"
                        style={{ height: `${rowHeight}rem`, boxShadow: "1px 0 0 0 rgb(243 244 246)" }}
                        title={subProduct.name}
                      >
                        {subProduct.name}
                      </div>

                      <div className="relative" style={{ gridColumn: `2 / span ${weekCols.length}`, height: `${rowHeight}rem` }}>
                        {/* Week column dividers (empty/future weeks greyed) */}
                        <div className="absolute inset-0 flex" style={{ pointerEvents: "none" }}>
                          {weekCols.map((w) => (
                            <div
                              key={`bg-${w.key}`}
                              className={`border-l border-gray-100 ${occupied.has(w.key) ? "" : "bg-gray-50/60"}`}
                              style={{ width: `${widthOf(w)}px`, flexShrink: 0 }}
                            />
                          ))}
                        </div>

                        {/* Pills */}
                        {placed.map(({ init, left, width }) => {
                          const laneIdx = laneOf.get(init.id) ?? 0;
                          const weeksSpanned = Math.max(1, Math.round(width / WEEK_WIDE));
                          return (
                            <div
                              key={init.id}
                              className="absolute box-border"
                              style={{
                                left: `${left}px`,
                                width: `${width}px`,
                                top: `${ROW_PADDING + laneIdx * (CARD_HEIGHT + CARD_GAP)}rem`,
                                paddingLeft: "0.25rem",
                                paddingRight: "0.25rem",
                                height: `${CARD_HEIGHT}rem`,
                              }}
                            >
                              <InitiativeCard
                                initiative={init}
                                onClick={() => handleInitiativeClick(init)}
                                quarterSpan={weeksSpanned}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Legend */}
      <div className="bg-background-secondary border-t border-gray-200 p-4">
        <div className="flex items-center gap-6 text-sm">
          <span className="font-medium text-text-secondary">CLASSIFICATION:</span>
          {classifications.map((classification) => (
            <div key={classification.id} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: classification.color }} />
              <span className="text-text-secondary">{classification.badgeLabel}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className="text-text-secondary">DEPENDENCIES</span>
          </div>
        </div>
      </div>
    </div>
  );
}
